import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { fmtDate } from "../lib/format";
import api, { pollO365WithProgress } from "../lib/api";
import { toast } from "sonner";
import { Gear, Envelope, ArrowsClockwise, Pause, Play, Trash, Warning, DownloadSimple, UploadSimple } from "@phosphor-icons/react";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [sync, setSync] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [form, setForm] = useState({
    enabled: false,
    email_address: "",
    app_password: "",
    imap_host: "imap.gmail.com",
    imap_port: 993,
  });
  const [o365, setO365] = useState({
    enabled: false,
    mailbox: "",
    backfill_days: 14,
    attachments_only: true,
    last_polled_at: null,
  });
  const [o365Status, setO365Status] = useState(null);
  const _daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);
  const [repoll, setRepoll] = useState({ since_date: _daysAgo(3), running: false, result: null });
  const [migBusy, setMigBusy] = useState("");
  const [migResult, setMigResult] = useState(null);
  const [oneTime, setOneTime] = useState({ busy: "", reconcile: null, cleanups: null, restore: null, dedup: null });
  const [diagCp, setDiagCp] = useState("");
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  const runDiagnose = async () => {
    if (!diagCp.trim()) return;
    setDiagBusy(true); setDiag(null);
    try {
      const r = await api.get("/admin/onetime/po-dedup/diagnose", { params: { custpo: diagCp.trim() } });
      setDiag(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Diagnose failed");
    } finally {
      setDiagBusy(false);
    }
  };

  const [diagVoc, setDiagVoc] = useState("");
  const [diagTp, setDiagTp] = useState(null);
  const [diagTpBusy, setDiagTpBusy] = useState(false);

  const runDiagnoseTp = async () => {
    if (!diagVoc.trim()) return;
    setDiagTpBusy(true); setDiagTp(null);
    try {
      const r = await api.get("/admin/dispatch/diagnose-tp", { params: { voc_no: diagVoc.trim() } });
      setDiagTp(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Diagnose failed");
    } finally {
      setDiagTpBusy(false);
    }
  };

  const [icBusy, setIcBusy] = useState(false);
  const [icResult, setIcResult] = useState(null);
  const runIcodeCanon = async (apply) => {
    setIcBusy(true);
    try {
      const r = await api.post(`/admin/onetime/icode-canon?apply=${apply ? "true" : "false"}`);
      setIcResult(r.data);
      if (apply) toast.success(`Normalized ${r.data.changed_lines} line(s) across ${r.data.changed_docs} PO(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Normalize failed");
    } finally {
      setIcBusy(false);
    }
  };

  const [poQ, setPoQ] = useState("");
  const [poMatches, setPoMatches] = useState(null);
  const [poBusy, setPoBusy] = useState(false);
  const findPo = async () => {
    if (!poQ.trim()) return;
    setPoBusy(true); setPoMatches(null);
    try {
      const r = await api.get("/admin/po/find", { params: { q: poQ.trim() } });
      setPoMatches(r.data.matches || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Find failed");
    } finally {
      setPoBusy(false);
    }
  };
  const deletePo = async (m) => {
    if (!window.confirm(`Delete PO ${m.ms_pono || m.po_number} — ${m.customer_name}? This removes the app record.`)) return;
    try {
      await api.delete(`/po/${m.id}`, { params: { reason: "manual admin delete (wrong PO)" } });
      toast.success("PO deleted");
      setPoMatches((prev) => (prev || []).filter((x) => x.id !== m.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Delete failed");
    }
  };

  const runRestore = async (apply) => {
    if (apply && !window.confirm("Re-insert genuinely-orphaned invoice lines that an earlier cleanup deleted? Only parts absent from the surviving invoice are restored — no shipped qty is double-counted. Safe to re-run.")) return;
    setOneTime((s) => ({ ...s, busy: `restore:${apply ? "apply" : "preview"}` }));
    try {
      const r = await api.post("/admin/onetime/restore-mirror-loss", { apply });
      setOneTime((s) => ({ ...s, restore: r.data }));
      toast.success(apply ? `Restored ${r.data.docs_restored} invoice(s) / ${r.data.orphan_lines_restored} line(s)` : "Preview ready (nothing changed)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setOneTime((s) => ({ ...s, busy: "" }));
    }
  };

  const runOneTime = async (which, apply) => {
    const ep = which === "reconcile" ? "/admin/onetime/avm-reconcile"
      : which === "dedup" ? "/admin/onetime/po-dedup"
      : "/admin/onetime/data-cleanups";
    if (apply && !window.confirm("Apply these changes to the CURRENT environment's database? A backup is saved and the action is repeat-safe.")) return;
    setOneTime((s) => ({ ...s, busy: `${which}:${apply ? "apply" : "preview"}` }));
    try {
      const start = await api.post(ep, { apply });
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("No job id");
      // poll the background job (heavy ERP scan runs server-side; avoids proxy timeout)
      let job = null;
      for (let i = 0; i < 150; i++) {          // up to ~5 min
        await new Promise((r) => setTimeout(r, 2000));
        const st = await api.get(`/admin/onetime/job/${jobId}`);
        job = st.data;
        if (job.status !== "running") break;
      }
      if (!job || job.status === "running") throw new Error("Still running — check again shortly");
      if (job.status === "error") throw new Error(job.error || "Job failed");
      setOneTime((s) => ({ ...s, [which]: job.result }));
      toast.success(apply ? "Applied — see the result below" : "Preview ready (nothing changed)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setOneTime((s) => ({ ...s, busy: "" }));
    }
  };

  const exportTracking = async () => {
    setMigBusy("export");
    try {
      const r = await api.get("/admin/tracking/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "container_tracking_export.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Tracking export downloaded");
    } catch (e) { toast.error("Export failed"); }
    finally { setMigBusy(""); }
  };
  const importTracking = async (file) => {
    if (!file) return;
    setMigBusy("import"); setMigResult(null);
    try {
      const parsed = JSON.parse(await file.text());
      const records = Array.isArray(parsed) ? parsed : (parsed.records || []);
      if (!records.length) { toast.error("No records found in that file"); return; }
      const r = await api.post("/admin/tracking/import", { records, overwrite: true });
      setMigResult(r.data);
      toast.success(`Imported ${r.data.inserted} new + ${r.data.updated} updated (0 credits)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed — is it a valid export file?"); }
    finally { setMigBusy(""); }
  };

  const loadSync = () => api.get("/sync/status").then((r) => setSync(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings/gmail").then((r) => {
      setSettings(r.data);
      setForm((f) => ({
        ...f,
        enabled: r.data.enabled,
        email_address: r.data.email_address || "",
        imap_host: r.data.imap_host || "imap.gmail.com",
        imap_port: r.data.imap_port || 993,
      }));
    });
    loadSync();
  }, []);

  useEffect(() => {
    api.get("/settings/o365").then((r) => setO365((o) => ({ ...o, ...r.data }))).catch(() => {});
    // Reflect any in-flight (or last) re-ingest so the button isn't stuck disabled.
    pollRepollStatus(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveO365 = async () => {
    if (!o365.mailbox) return toast.error("Mailbox address is required");
    try {
      await api.put("/settings/o365", {
        mailbox: o365.mailbox,
        enabled: o365.enabled,
        backfill_days: Number(o365.backfill_days) || 0,
        attachments_only: o365.attachments_only,
      });
      toast.success("Microsoft 365 settings saved");
      const r = await api.get("/settings/o365");
      setO365((o) => ({ ...o, ...r.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const [o365Polling, setO365Polling] = useState(false);
  const pollO365 = async () => {
    setO365Polling(true);
    try {
      const d = await pollO365WithProgress();
      const rec = d.recovered_carting ? `, recovered ${d.recovered_carting}` : "";
      toast.success(`Polled: ${d.processed || 0} new, ${d.skipped_already_ingested || 0} already seen${rec}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Poll failed");
    } finally { setO365Polling(false); }
  };

  const checkO365 = async () => {
    if (!o365.mailbox) return toast.error("Enter a mailbox address first");
    setO365Status({ checking: true });
    try {
      // Persist the current form first so the server-side check reads fresh data
      // (the check inspects the saved DB record, not the in-memory form).
      await api.put("/settings/o365", {
        mailbox: o365.mailbox,
        enabled: o365.enabled,
        backfill_days: Number(o365.backfill_days) || 0,
        attachments_only: o365.attachments_only,
      });
      const r = await api.get("/settings/o365/check");
      setO365Status(r.data);
      if (r.data.ok) toast.success("Mailbox is readable — access confirmed");
      else toast.error(`Not readable yet (HTTP ${r.data.status ?? "?"})`);
    } catch (e) {
      setO365Status({ ok: false, error: e.response?.data?.detail || "check failed" });
      toast.error(e.response?.data?.detail || "Check failed");
    }
  };

  // Poll the background re-ingest status until it finishes; used by runRepoll AND on mount
  // (so the button self-heals / reflects reality even after a page refresh).
  const pollRepollStatus = (announce = false) => {
    const poll = async () => {
      try {
        const s = await api.get("/emails/o365/repoll-status");
        if (s.data && s.data.running) {
          setRepoll((r) => ({ ...r, running: true }));
          setTimeout(poll, 3000);
        } else {
          setRepoll((r) => ({ ...r, running: false, result: s.data }));
          if (announce) {
            if (s.data?.error) toast.error(`Re-ingest finished with error: ${s.data.error}`);
            else toast.success(`Re-ingest complete — ${s.data?.reingested ?? 0} email(s) re-read`);
          }
          const o = await api.get("/settings/o365");
          setO365((prev) => ({ ...prev, ...o.data }));
        }
      } catch {
        setTimeout(poll, 3000);
      }
    };
    poll();
  };

  const runRepoll = async () => {
    if (!repoll.since_date) return toast.error("Pick a start date first");
    setRepoll((r) => ({ ...r, running: true, result: null }));
    try {
      const res = await api.post("/emails/o365/repoll-reingest", { since_date: repoll.since_date });
      toast.success(`Cleared ${res.data.cleared_emails} email(s); re-ingest running in background…`);
      setTimeout(() => pollRepollStatus(true), 2500);
    } catch (e) {
      setRepoll((r) => ({ ...r, running: false }));
      toast.error(e.response?.data?.detail || "Re-poll failed");
    }
  };

  const pauseSync = async () => {
    setBusy("pause");
    try { await api.post("/sync/pause"); toast.success("Auto-sync paused"); await loadSync(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const resumeSync = async () => {
    setBusy("resume");
    try { await api.post("/sync/resume?run_now=true"); toast.success("Auto-sync resumed — running a sync now"); await loadSync(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const runNow = async () => {
    setBusy("run");
    try { const r = await api.post("/sync/run"); toast.success(`Sync done · ${r.data.po_inserted ?? 0} new POs`); await loadSync(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const cleanup = async () => {
    if (confirmText !== "DELETE") return toast.error('Type DELETE to confirm');
    setBusy("cleanup");
    try {
      const r = await api.post("/sync/cleanup");
      const total = Object.values(r.data.wiped || {}).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${total} records. Auto-sync paused.`);
      setConfirmText("");
      await loadSync();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const save = async () => {
    if (!form.email_address || !form.app_password) {
      return toast.error("Email and app password are required");
    }
    try {
      await api.put("/settings/gmail", form);
      toast.success("Gmail settings saved");
      api.get("/settings/gmail").then((r) => setSettings(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const syncOn = sync?.enabled;

  return (
    <Layout breadcrumb={["Settings"]}>
      <div className="jde-toolbar">
        <Gear size={14} weight="bold" />
        <span style={{ fontWeight: 700, fontSize: 12 }}>SYSTEM SETTINGS</span>
      </div>

      {/* MS SQL sync control */}
      <div className="jde-panel" style={{ marginTop: 12 }} data-testid="sync-control-panel">
        <div className="jde-panel-header">
          <span><ArrowsClockwise size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} /> MS SQL → MONGODB SYNC</span>
          <span data-testid="sync-state-badge" style={{
            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 3,
            background: syncOn ? "#DCFCE7" : "#FEE2E2", color: syncOn ? "#166534" : "#991B1B",
          }}>
            {sync == null ? "…" : syncOn ? "● RUNNING (every 30 min)" : "❚❚ PAUSED"}
          </span>
        </div>
        <div className="jde-panel-body">
          <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
            Last sync: <strong>{sync?.at ? sync.at.slice(0, 19).replace("T", " ") : "Never"}</strong>
            {sync?.po_inserted != null && (
              <span> · {sync.po_inserted} new / {sync.po_updated} updated / {sync.po_revised} revised POs</span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {syncOn ? (
              <button className="jde-btn" onClick={pauseSync} disabled={busy === "pause"} data-testid="btn-pause-sync">
                <Pause size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />
                {busy === "pause" ? "Pausing…" : "Stop Auto-Sync"}
              </button>
            ) : (
              <button className="jde-btn jde-btn-primary" onClick={resumeSync} disabled={busy === "resume"} data-testid="btn-resume-sync">
                <Play size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />
                {busy === "resume" ? "Starting…" : "Start Auto-Sync"}
              </button>
            )}
            <button className="jde-btn" onClick={runNow} disabled={busy === "run"} data-testid="btn-run-sync-now">
              <ArrowsClockwise size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />
              {busy === "run" ? "Syncing…" : "Run Sync Now"}
            </button>
          </div>
          {!syncOn && (
            <div style={{ marginTop: 10, fontSize: 11, color: "#92400E", background: "#FEF3C7", border: "1px solid #FDE68A", padding: 8 }}>
              Auto-sync is paused. SAP data will <strong>not</strong> refresh until you click “Start Auto-Sync”.
            </div>
          )}
        </div>
      </div>

      {/* Danger zone */}
      <div className="jde-panel" style={{ marginTop: 12, borderColor: "#FCA5A5" }} data-testid="danger-zone-panel">
        <div className="jde-panel-header" style={{ background: "#FEF2F2", color: "#991B1B" }}>
          <span><Warning size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} /> DANGER ZONE — DELETE ALL ORDER DATA</span>
        </div>
        <div className="jde-panel-body">
          <div style={{ fontSize: 12, marginBottom: 10 }}>
            Permanently deletes <strong>all purchase orders & invoices</strong> (both SAP-synced and Excel-imported),
            plus customers / events / email logs. <strong>Users are preserved.</strong> Auto-sync is paused automatically
            so SAP data does not flow back in. The SAP database itself is never touched. <strong>This cannot be undone.</strong>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              className="jde-input"
              style={{ width: 200 }}
              placeholder='Type DELETE to confirm'
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="cleanup-confirm-input"
            />
            <button
              className="jde-btn"
              style={{ background: "#DC2626", color: "#fff", borderColor: "#DC2626", opacity: confirmText === "DELETE" ? 1 : 0.5 }}
              onClick={cleanup}
              disabled={confirmText !== "DELETE" || busy === "cleanup"}
              data-testid="btn-cleanup-all"
            >
              <Trash size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />
              {busy === "cleanup" ? "Deleting…" : "Delete All Order Data"}
            </button>
          </div>
        </div>
      </div>

      <div className="jde-panel" style={{ marginTop: 12 }}>
        <div className="jde-panel-header">
          <span><Envelope size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} /> GMAIL INGESTION (IMAP)</span>
          <span style={{ fontSize: 11, fontWeight: 400 }} className="muted">
            Last polled: {settings?.last_polled_at ? settings.last_polled_at.slice(0, 19).replace("T", " ") : "Never"}
          </span>
        </div>
        <div className="jde-panel-body">
          <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", padding: 10, fontSize: 11, color: "#92400E", marginBottom: 12 }}>
            <strong>Gmail App Password required.</strong> Go to your Google Account → Security → 2-Step Verification → App Passwords.
            Create a new app password (16 chars) and paste below. Do NOT use your main Gmail password.
            Forward customer POs and SAP invoices to this mailbox; the system will classify them with AI.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label className="jde-label">Gmail Address</label>
              <input
                className="jde-input"
                value={form.email_address}
                onChange={(e) => setForm({ ...form, email_address: e.target.value })}
                data-testid="gmail-email"
                placeholder="orders@yourcompany.com"
              />
            </div>
            <div>
              <label className="jde-label">App Password (16 chars)</label>
              <input
                className="jde-input text-mono"
                type="password"
                value={form.app_password}
                onChange={(e) => setForm({ ...form, app_password: e.target.value })}
                data-testid="gmail-pwd"
                placeholder="xxxx xxxx xxxx xxxx"
              />
            </div>
            <div>
              <label className="jde-label">IMAP Host</label>
              <input
                className="jde-input"
                value={form.imap_host}
                onChange={(e) => setForm({ ...form, imap_host: e.target.value })}
                data-testid="gmail-host"
              />
            </div>
            <div>
              <label className="jde-label">IMAP Port</label>
              <input
                className="jde-input num"
                type="number"
                value={form.imap_port}
                onChange={(e) => setForm({ ...form, imap_port: Number(e.target.value) })}
                data-testid="gmail-port"
              />
            </div>
          </div>

          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
            <label style={{ fontSize: 12, display: "inline-flex", gap: 6, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                data-testid="gmail-enabled"
              />
              Enable polling
            </label>
            <div style={{ flex: 1 }} />
            <button className="jde-btn jde-btn-primary" onClick={save} data-testid="gmail-save">Save Settings</button>
          </div>
        </div>
      </div>

      <div className="jde-panel" style={{ marginTop: 12 }} data-testid="o365-panel">
        <div className="jde-panel-header">
          <span><Envelope size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} /> MICROSOFT 365 INGESTION (GRAPH)</span>
          <span style={{ fontSize: 11, fontWeight: 400 }} className="muted">
            Last polled: {o365?.last_polled_at ? o365.last_polled_at.slice(0, 19).replace("T", " ") : "Never"}
          </span>
        </div>
        <div className="jde-panel-body">
          <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", padding: 10, fontSize: 11, color: "#1E40AF", marginBottom: 12 }}>
            Reads a Microsoft 365 mailbox via Graph using the existing Azure app (app-only, read-only).
            The mailbox must receive the PO emails (e.g. a shared mailbox that is a member of your CS Team list),
            and the Azure app needs <strong>Mail.Read</strong> plus access-policy permission to that mailbox.
            Use <strong>Check access</strong> to confirm before enabling.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
            <div>
              <label className="jde-label">Mailbox Address</label>
              <input
                className="jde-input"
                value={o365.mailbox}
                onChange={(e) => setO365({ ...o365, mailbox: e.target.value })}
                data-testid="o365-mailbox"
                placeholder="po-intake@yourcompany.com"
              />
            </div>
            <div>
              <label className="jde-label">Backfill on first run (days)</label>
              <input
                className="jde-input num"
                type="number"
                value={o365.backfill_days}
                onChange={(e) => setO365({ ...o365, backfill_days: Number(e.target.value) })}
                data-testid="o365-backfill-days"
              />
            </div>
          </div>

          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <label style={{ fontSize: 12, display: "inline-flex", gap: 6, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={o365.enabled}
                onChange={(e) => setO365({ ...o365, enabled: e.target.checked })}
                data-testid="o365-enabled"
              />
              Enable polling
            </label>
            <label style={{ fontSize: 12, display: "inline-flex", gap: 6, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={o365.attachments_only}
                onChange={(e) => setO365({ ...o365, attachments_only: e.target.checked })}
                data-testid="o365-attachments-only"
              />
              Only emails with attachments
            </label>
            <div style={{ flex: 1 }} />
            <button className="jde-btn" onClick={pollO365} disabled={o365Polling} data-testid="o365-poll-now">
              {o365Polling ? "Polling…" : "Poll now"}
            </button>
            <button className="jde-btn" onClick={checkO365} data-testid="o365-check">
              {o365Status?.checking ? "Checking…" : "Check access"}
            </button>
            <button className="jde-btn jde-btn-primary" onClick={saveO365} data-testid="o365-save">Save Settings</button>
          </div>

          {o365Status && !o365Status.checking && (
            <div data-testid="o365-check-result" style={{
              marginTop: 10, fontSize: 11, padding: 8,
              background: o365Status.ok ? "#ECFDF5" : "#FEF2F2",
              border: `1px solid ${o365Status.ok ? "#A7F3D0" : "#FECACA"}`,
              color: o365Status.ok ? "#065F46" : "#991B1B",
            }}>
              {o365Status.ok
                ? "✓ Access confirmed — the app can read this mailbox."
                : `✗ Not readable yet (HTTP ${o365Status.status ?? "?"}). If you just changed permissions, Microsoft can take up to ~30 min to apply them. Details: ${o365Status.error || ""}`}
            </div>
          )}

          {/* Re-poll & re-ingest from a chosen date */}
          <div style={{ marginTop: 16, borderTop: "1px solid #E5E7EB", paddingTop: 14 }} data-testid="o365-repoll-section">
            <label className="jde-label" style={{ marginBottom: 6 }}>Re-poll &amp; re-ingest from date</label>
            <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", padding: 10, fontSize: 11, color: "#92400E", marginBottom: 10 }}>
              Re-reads O365 emails received on/after the chosen date and re-processes them under the
              current intake rules. Emails whose PO is already <strong>confirmed / pushed to ERP / shipped</strong>
              are left untouched; only not-yet-confirmed intake POs and their email records are cleared so they
              can be re-read. The live ERP is never modified.
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <div>
                <label className="jde-label">Start date</label>
                <input
                  className="jde-input"
                  type="date"
                  value={repoll.since_date}
                  max={_daysAgo(0)}
                  onChange={(e) => setRepoll((r) => ({ ...r, since_date: e.target.value }))}
                  data-testid="o365-repoll-date"
                />
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {[1, 3, 7].map((n) => (
                  <button key={n} className="jde-btn jde-btn-sm" onClick={() => setRepoll((r) => ({ ...r, since_date: _daysAgo(n) }))}
                          data-testid={`o365-repoll-preset-${n}`}>
                    {n}d
                  </button>
                ))}
              </div>
              <div style={{ flex: 1 }} />
              <button className="jde-btn jde-btn-primary" onClick={runRepoll} disabled={repoll.running || !o365.enabled}
                      data-testid="o365-repoll-run" title={!o365.enabled ? "Enable & save O365 first" : undefined}>
                {repoll.running ? "Re-ingesting…" : "Re-poll & re-ingest"}
              </button>
            </div>
            {repoll.running && (
              <div data-testid="o365-repoll-running" style={{
                marginTop: 10, fontSize: 11, padding: 8, background: "#EFF6FF",
                border: "1px solid #BFDBFE", color: "#1E40AF",
              }}>
                Re-ingest running in the background — this can take a couple of minutes for many
                emails. You can leave this page; results appear in Intake Review.
              </div>
            )}
            {!repoll.running && repoll.result && (
              <div data-testid="o365-repoll-result" style={{
                marginTop: 10, fontSize: 11, padding: 8,
                background: repoll.result.error ? "#FEF2F2" : "#ECFDF5",
                border: `1px solid ${repoll.result.error ? "#FECACA" : "#A7F3D0"}`,
                color: repoll.result.error ? "#991B1B" : "#065F46",
              }}>
                {repoll.result.error
                  ? `✗ Re-ingest error: ${repoll.result.error}`
                  : <>
                      ✓ Since {fmtDate(repoll.result.since)} — re-read <strong>{repoll.result.reingested ?? 0}</strong> email(s),
                      booked <strong>{repoll.result.pos_created ?? 0}</strong> PO(s) into review
                      (cleared {repoll.result.cleared_emails ?? 0}, kept {repoll.result.kept_finalized ?? 0} finalized).
                      {" "}
                      <Link to="/intake-review" data-testid="o365-repoll-goto-review" style={{ fontWeight: 700, textDecoration: "underline", color: "#065F46" }}>
                        View in PO Intake Review →
                      </Link>
                    </>}
              </div>
            )}
            {!repoll.running && repoll.result && (repoll.result.failures?.length > 0) && (
              <div data-testid="o365-repoll-failures" style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#991B1B", marginBottom: 4 }}>
                  <Warning size={12} weight="bold" style={{ display: "inline", marginRight: 4 }} />
                  Didn&apos;t extract ({repoll.result.failures.length}) — needs review
                </div>
                <div style={{ border: "1px solid #FECACA", borderRadius: 4, overflow: "hidden" }}>
                  <table className="jde-table" style={{ fontSize: 11, width: "100%" }}>
                    <thead>
                      <tr style={{ background: "#FEF2F2" }}>
                        <th style={{ textAlign: "left", padding: "4px 8px" }}>Subject</th>
                        <th style={{ textAlign: "left", padding: "4px 8px" }}>From</th>
                        <th style={{ textAlign: "left", padding: "4px 8px" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repoll.result.failures.map((f) => (
                        <tr key={f.id} data-testid={`o365-repoll-failure-${f.id}`} style={{ borderTop: "1px solid #FEE2E2" }}>
                          <td style={{ padding: "4px 8px" }}>{f.subject}</td>
                          <td style={{ padding: "4px 8px", color: "#6B7280" }}>{f.sender}</td>
                          <td style={{ padding: "4px 8px" }}>
                            <span className="jde-badge" style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 9 }}>
                              {f.status === "low_confidence" ? "Low confidence" : "Failed"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Link to="/failures" data-testid="o365-repoll-goto-failures" style={{ fontSize: 11, fontWeight: 600, textDecoration: "underline", color: "#991B1B" }}>
                  Open Failures to retry →
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="jde-panel" style={{ marginTop: 12 }} data-testid="tracking-migration-panel">
        <div className="jde-panel-header"><span>CONTAINER TRACKING — MIGRATION</span></div>
        <div className="jde-panel-body">
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
            Move cached container-tracking records between environments (e.g. preview → production)
            without re-tracking. Import upserts by container number and <b>never calls FindTeu</b>, so
            it costs <b>zero</b> tracking credits. On the source env click <b>Export</b>, then upload
            that file with <b>Import</b> on the target env.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <button className="jde-btn" onClick={exportTracking} disabled={migBusy === "export"} data-testid="tracking-export-btn">
              <DownloadSimple size={15} style={{ marginRight: 6, verticalAlign: "-2px" }} />
              {migBusy === "export" ? "Exporting…" : "Export tracking JSON"}
            </button>
            <label className="jde-btn jde-btn-primary" data-testid="tracking-import-label" style={{ cursor: "pointer" }}>
              <UploadSimple size={15} style={{ marginRight: 6, verticalAlign: "-2px" }} />
              {migBusy === "import" ? "Importing…" : "Import tracking JSON"}
              <input type="file" accept="application/json,.json" style={{ display: "none" }}
                data-testid="tracking-import-input"
                onChange={(e) => { importTracking(e.target.files && e.target.files[0]); e.target.value = ""; }} />
            </label>
          </div>
          {migResult && (
            <div data-testid="tracking-import-result" style={{ marginTop: 10, fontSize: 12, background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 8, padding: 10, color: "#065F46" }}>
              Imported <b>{migResult.inserted}</b> new · <b>{migResult.updated}</b> updated · {migResult.skipped} skipped · <b>{migResult.collection_total_now}</b> total cached now (0 FindTeu credits used).
            </div>
          )}
        </div>
      </div>

      <div className="jde-panel" style={{ marginTop: 12, borderColor: "#C7D2FE" }} data-testid="onetime-panel">
        <div className="jde-panel-header" style={{ background: "#EEF2FF", color: "#3730A3" }}>
          <span>ONE TIME STUFF — data fixes (super admin)</span>
        </div>
        <div className="jde-panel-body">
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
            One-off data corrections that must be applied on <b>this environment&apos;s database</b>.
            Run them on <b>production</b> after deploy. Each shows a <b>Preview</b> first (counts only,
            changes nothing); <b>Apply</b> writes the change and saves a backup. Safe to re-run.
          </p>

          {/* Step 1 — AVM reconcile */}
          <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: 12, marginBottom: 12 }} data-testid="onetime-reconcile-card">
            <div style={{ fontWeight: 700, fontSize: 13 }}>1 · AVM invoice reconcile</div>
            <div className="muted" style={{ fontSize: 12, margin: "4px 0 10px" }}>
              Re-resolves AVM transfer-priced invoices to their real end customer and removes stale
              biller copies. (Same engine as &quot;Run auto-sync now&quot;.)
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="jde-btn" onClick={() => runOneTime("reconcile", false)}
                disabled={oneTime.busy.startsWith("reconcile")} data-testid="onetime-reconcile-preview">
                {oneTime.busy === "reconcile:preview" ? "Previewing…" : "Preview"}
              </button>
              <button className="jde-btn jde-btn-primary" onClick={() => runOneTime("reconcile", true)}
                disabled={oneTime.busy.startsWith("reconcile") || !oneTime.reconcile} data-testid="onetime-reconcile-apply">
                {oneTime.busy === "reconcile:apply" ? "Applying…" : "Apply"}
              </button>
            </div>
            {oneTime.reconcile && (
              <div data-testid="onetime-reconcile-result" style={{ marginTop: 10, fontSize: 12, background: oneTime.reconcile.applied ? "#ECFDF5" : "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: 10 }}>
                {oneTime.reconcile.applied ? "APPLIED" : "PREVIEW"} · scanned {oneTime.reconcile.summary?.erp_gst_scanned} · candidates {oneTime.reconcile.summary?.candidates} · upserted {oneTime.reconcile.summary?.upserted} · removed {oneTime.reconcile.summary?.removed} · AVM 'erp' copies {oneTime.reconcile.source_erp_avm_before} → {oneTime.reconcile.source_erp_avm_after}
              </div>
            )}
          </div>

          {/* Step 2 — data cleanups */}
          <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: 12 }} data-testid="onetime-cleanups-card">
            <div style={{ fontWeight: 700, fontSize: 13 }}>2 · Duplicate &amp; bundled-line cleanup</div>
            <div className="muted" style={{ fontSize: 12, margin: "4px 0 10px" }}>
              Stamps the &quot;via TP: AVM&quot; badge on end-customer invoices, deletes the paused-mirror
              <code> source=None </code> AVM duplicate copies, and drops the wrongly-bundled line from 3 Excel invoices.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="jde-btn" onClick={() => runOneTime("cleanups", false)}
                disabled={oneTime.busy.startsWith("cleanups")} data-testid="onetime-cleanups-preview">
                {oneTime.busy === "cleanups:preview" ? "Previewing…" : "Preview"}
              </button>
              <button className="jde-btn jde-btn-primary" onClick={() => runOneTime("cleanups", true)}
                disabled={oneTime.busy.startsWith("cleanups") || !oneTime.cleanups} data-testid="onetime-cleanups-apply">
                {oneTime.busy === "cleanups:apply" ? "Applying…" : "Apply"}
              </button>
            </div>
            {oneTime.cleanups && (
              <div data-testid="onetime-cleanups-result" style={{ marginTop: 10, fontSize: 12, background: oneTime.cleanups.applied ? "#ECFDF5" : "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: 10 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{oneTime.cleanups.applied ? "APPLIED" : "PREVIEW"}{oneTime.cleanups.backup_id ? ` · backup ${oneTime.cleanups.backup_id}` : ""}</div>
                <div>Badge stamped: <b>{oneTime.cleanups.ops?.stamp_badge}</b> · re-attributed: <b>{oneTime.cleanups.ops?.sourcenone_reattributed}</b> · bundled lines fixed: <b>{oneTime.cleanups.ops?.bundled_lines_fixed}</b></div>
                <div style={{ marginTop: 4 }}>Mirror duplicates deleted: <b>{oneTime.cleanups.ops?.mirror_dup_deleted}</b> · trimmed to unique lines: <b>{oneTime.cleanups.ops?.mirror_trimmed}</b> · unique mirror kept: <b>{oneTime.cleanups.ops?.mirror_kept_unique}</b> · transfer-priced kept: <b>{oneTime.cleanups.ops?.mirror_kept_tp}</b></div>
              </div>
            )}
          </div>

          {/* Step 3 — phantom PO de-dup */}
          <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: 12, marginTop: 12 }} data-testid="onetime-dedup-card">
            <div style={{ fontWeight: 700, fontSize: 13 }}>3 · Phantom / duplicate PO de-dup</div>
            <div className="muted" style={{ fontSize: 12, margin: "4px 0 10px" }}>
              Silently suppresses duplicate POs so they drop out of Open Orders: the <b>crosswalk</b> case
              (same order booked under two customer-PO numbers, proven by a shared shipment invoice) and the
              <code> source=None </code> <b>ERP-mirror</b> copy of an already-shipped order. Non-destructive
              (a reversible <code>duplicate_of</code> tag), read-only on the ERP, repeat-safe. This is the same
              job the 6-hourly sync runs — use it to apply immediately on this environment.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="jde-btn" onClick={() => runOneTime("dedup", false)}
                disabled={oneTime.busy.startsWith("dedup")} data-testid="onetime-dedup-preview">
                {oneTime.busy === "dedup:preview" ? "Previewing…" : "Preview"}
              </button>
              <button className="jde-btn jde-btn-primary" onClick={() => runOneTime("dedup", true)}
                disabled={oneTime.busy.startsWith("dedup") || !oneTime.dedup} data-testid="onetime-dedup-apply">
                {oneTime.busy === "dedup:apply" ? "Applying…" : "Apply"}
              </button>
            </div>
            {oneTime.dedup && (
              <div data-testid="onetime-dedup-result" style={{ marginTop: 10, fontSize: 12, background: oneTime.dedup.applied ? "#ECFDF5" : "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: 10 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{oneTime.dedup.applied ? "APPLIED" : "PREVIEW"} · {oneTime.dedup.total_flagged} phantom PO(s) {oneTime.dedup.applied ? "suppressed" : "found"}</div>
                <div>Crosswalk: <b>{oneTime.dedup.crosswalk?.flagged}</b> (of {oneTime.dedup.crosswalk?.pairs_checked} pairs) · Mirror: <b>{oneTime.dedup.mirror?.flagged}</b> (of {oneTime.dedup.mirror?.groups_checked} groups)</div>
                {oneTime.dedup.crosswalk?.error && <div style={{ color: "#B45309", marginTop: 4 }}>Crosswalk note: {oneTime.dedup.crosswalk.error}</div>}
                {(oneTime.dedup.crosswalk?.merges?.length > 0 || oneTime.dedup.mirror?.merges?.length > 0) && (
                  <div style={{ marginTop: 8, maxHeight: 180, overflow: "auto", borderTop: "1px solid #E2E8F0", paddingTop: 6 }}>
                    {(oneTime.dedup.crosswalk?.merges || []).slice(0, 40).map((m, i) => (
                      <div key={`x${i}`}>· {m.pcode} <b>{m.phantom_custpo || m.phantom_pono}</b> → kept {m.kept_custpo || m.kept_pono}{m.single_side ? " (single-side)" : ""}</div>
                    ))}
                    {(oneTime.dedup.mirror?.merges || []).slice(0, 40).map((m, i) => (
                      <div key={`m${i}`}>· {m.pcode} mirror <b>{m.phantom_custpo}</b> (PO {m.phantom_pono}) → shipped twin(s)</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Diagnose a single PO (read-only ground truth for THIS environment) */}
            <div style={{ marginTop: 12, borderTop: "1px dashed #E2E8F0", paddingTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Diagnose a PO <span className="muted" style={{ fontWeight: 400 }}>— why is / isn't a specific customer PO suppressed here?</span></div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input className="jde-input" style={{ width: 220 }} placeholder="Customer PO e.g. 0986240225" value={diagCp}
                  onChange={(e) => setDiagCp(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runDiagnose()} data-testid="dedup-diagnose-input" />
                <button className="jde-btn" onClick={runDiagnose} disabled={diagBusy || !diagCp.trim()} data-testid="dedup-diagnose-btn">
                  {diagBusy ? "Checking…" : "Diagnose"}
                </button>
              </div>
              {diag && (
                <div data-testid="dedup-diagnose-result" style={{ marginTop: 8, fontSize: 12, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: 10 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{diag.custpo}: {diag.verdict}</div>
                  <div className="muted">Ordered <b>{diag.ordered_total}</b> · ERP shipped for this PO <b>{diag.erp_shipped_for_custpo}</b> · crosswalk PO(s): {(diag.crosswalk_ponos || []).join(", ") || "none"}</div>
                  {(diag.twins || []).map((t, i) => (
                    <div key={i} className="muted">twin PO {t.pono}: doc present = {String(t.doc_present)}, shipped = {String(t.any_shipped)}</div>
                  ))}
                  <div style={{ marginTop: 6 }}>
                    {(diag.docs || []).map((x, i) => (
                      <div key={i}>· src={String(x.source)} · pono {x.ms_pono} · {x.status} · ord {x.qty_ordered_total} / open {x.qty_pending_total} · {x.duplicate_of ? "SUPPRESSED (duplicate_of set)" : "active"}</div>
                    ))}
                    {(diag.docs || []).length === 0 && <div className="muted">No PO doc found in this environment.</div>}
                  </div>
                  {diag.erp_error && <div style={{ color: "#B45309", marginTop: 4 }}>ERP note: {diag.erp_error}</div>}
                </div>
              )}
            </div>

            {/* Diagnose a Dispatch voucher's "via TP" relabel (read-only) */}
            <div style={{ marginTop: 12, borderTop: "1px dashed #E2E8F0", paddingTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Diagnose a Dispatch <span className="muted" style={{ fontWeight: 400 }}>— why is a Voc# tagged "via TP: …"?</span></div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input className="jde-input" style={{ width: 220 }} placeholder="ERP Voc# e.g. 4470" value={diagVoc}
                  onChange={(e) => setDiagVoc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runDiagnoseTp()} data-testid="tp-diagnose-input" />
                <button className="jde-btn" onClick={runDiagnoseTp} disabled={diagTpBusy || !diagVoc.trim()} data-testid="tp-diagnose-btn">
                  {diagTpBusy ? "Checking…" : "Diagnose"}
                </button>
              </div>
              {diagTp && (
                <div data-testid="tp-diagnose-result" style={{ marginTop: 8, fontSize: 12, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: 10 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Voc# {diagTp.voc_no} · ERP customer {(diagTp.erp_po_dispatch?.[0]?.Pcode) || "—"} · invoice {diagTp.erp_status?.inv_no || "—"} → {diagTp.erp_status?.inv_pcode || "—"}</div>
                  {(diagTp.mongo_plans || []).length === 0 && <div className="muted">No Mongo dispatch plan found for this Voc# in this environment.</div>}
                  {(diagTp.mongo_plans || []).map((p, i) => (
                    <div key={i} style={{ marginBottom: 4 }}>
                      · plan …{(p.plan_id || "").slice(-6)} · booked <b>{p.customer_name}</b> ({p.pcode}) · tp_code={String(p.tp_code || "—")} · tp_name={String(p.tp_name || "—")}
                      {p.tp_probe?.would_relabel
                        ? <span style={{ color: "#B45309" }}> → RELABELED to "{p.tp_probe.swap_customer_to}", shown "via TP: {p.tp_probe.via_tp_label}" (matched {p.tp_probe.matched_key})</span>
                        : <span style={{ color: "#166534" }}> → no map relabel</span>}
                    </div>
                  ))}
                  {diagTp.invoice_tp_probe && (
                    <div style={{ marginTop: 4 }}>
                      · invoice-TP path (inv {diagTp.invoice_tp_probe.inv_no || "—"}): {diagTp.invoice_tp_probe.is_tp_invoice
                        ? <span>flagged is_tp → biller {String(diagTp.invoice_tp_probe.tp_name || diagTp.invoice_tp_probe.tp_code || "—")}
                            {diagTp.invoice_tp_probe.self_referential
                              ? <span style={{ color: "#166534" }}> — SELF-REFERENTIAL, badge suppressed ✓</span>
                              : <span style={{ color: "#B45309" }}> — would badge "via TP: {String(diagTp.invoice_tp_probe.tp_name)}"</span>}
                          </span>
                        : <span style={{ color: "#166534" }}> not a TP invoice → no badge</span>}
                    </div>
                  )}
                  {(diagTp.tp_contributors || []).length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontWeight: 700 }}>Plan(s) that seeded the matched TP key — clear the stray transfer-party value here:</div>
                      {diagTp.tp_contributors.map((c, i) => (
                        <div key={i} className="muted">· plan …{(c.plan_id || "").slice(-6)} (Voc {String(c.voc_no ?? "—")}) · tp_code={String(c.tp_code || "—")} · tp_name={String(c.tp_name || "—")} · end-cust {c.customer_name} ({c.pcode})</div>
                      ))}
                    </div>
                  )}
                  <div className="muted" style={{ marginTop: 6 }}>{diagTp.note}</div>
                  {diagTp.erp_error && <div style={{ color: "#B45309", marginTop: 4 }}>ERP note: {diagTp.erp_error}</div>}
                </div>
              )}
            </div>

            {/* One-time: normalize variant item codes (e.g. Legrand F-62 -> F62) */}
            <div style={{ marginTop: 12, borderTop: "1px dashed #E2E8F0", paddingTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Normalize item codes <span className="muted" style={{ fontWeight: 400 }}>— fold variant codes (e.g. F-62 → F62) so a part stops showing twice</span></div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="jde-btn" onClick={() => runIcodeCanon(false)} disabled={icBusy} data-testid="icode-canon-preview">
                  {icBusy ? "Working…" : "Preview"}
                </button>
                <button className="jde-btn jde-btn-primary" onClick={() => runIcodeCanon(true)} disabled={icBusy || !icResult || !icResult.changed_lines} data-testid="icode-canon-apply">
                  Apply{icResult && icResult.changed_lines ? ` (${icResult.changed_lines})` : ""}
                </button>
              </div>
              {icResult && (
                <div data-testid="icode-canon-result" style={{ marginTop: 8, fontSize: 12, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: 10 }}>
                  <div>{icResult.apply ? "Applied" : "Preview"} · scanned {icResult.scanned} POs · {icResult.changed_lines} line(s) in {icResult.changed_docs} PO(s) {icResult.apply ? "updated" : "would change"}.</div>
                  {(icResult.sample || []).slice(0, 8).map((s, i) => (
                    <div key={i} className="muted">· {s.field}: {s.from} → {s.to}</div>
                  ))}
                  {!icResult.changed_lines && <div className="muted">No variant codes found to fold.</div>}
                </div>
              )}
            </div>

            {/* Find & delete a wrong PO by number (e.g. FLT2 PO 0992774935) */}
            <div style={{ marginTop: 12, borderTop: "1px dashed #E2E8F0", paddingTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Delete a wrong PO <span className="muted" style={{ fontWeight: 400 }}>— find by PO# / customer-PO and remove the app record</span></div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input className="jde-input" style={{ width: 240 }} placeholder="PO # e.g. 0992774935" value={poQ}
                  onChange={(e) => setPoQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && findPo()} data-testid="po-find-input" />
                <button className="jde-btn" onClick={findPo} disabled={poBusy || !poQ.trim()} data-testid="po-find-btn">{poBusy ? "Finding…" : "Find"}</button>
              </div>
              {poMatches && (
                <div data-testid="po-find-result" style={{ marginTop: 8, fontSize: 12 }}>
                  {poMatches.length === 0 && <div className="muted">No matching PO found.</div>}
                  {poMatches.map((m) => (
                    <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: "8px 10px", marginBottom: 6 }}>
                      <div>
                        <div><b>{m.ms_pono || m.po_number}</b> · {m.customer_name} ({m.ms_pcode}) · <span className="muted">{m.source}</span></div>
                        <div className="muted">custPO {m.ms_custpo || "—"} · endPO2 {m.cust_po2 || "—"} · pending {m.qty_pending_total ?? "—"} · items {(m.lines || []).map((l) => `${l.icode}:${l.qty_open ?? l.qty_ordered}`).join(", ")}</div>
                      </div>
                      <button className="jde-btn" style={{ color: "#B91C1C", borderColor: "#FCA5A5" }} onClick={() => deletePo(m)} data-testid={`po-delete-${m.id}`}>Delete</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>


          {/* Step 3 — recover orphaned lines a prior cleanup deleted */}
          <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: 12, marginTop: 12 }} data-testid="onetime-restore-card">
            <div style={{ fontWeight: 700, fontSize: 13 }}>4 · Recover orphaned lines (data-loss recovery)</div>
            <div className="muted" style={{ fontSize: 12, margin: "4px 0 10px" }}>
              Re-inserts invoice lines that an <b>earlier</b> mirror cleanup deleted even though the part
              was absent from the surviving invoice (e.g. inv 400003 / PO 0104075). Matches by
              company + invoice# + part, so genuine Legrand SAP-PO twins are NOT re-added and no shipped
              qty is double-counted. Safe to re-run.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="jde-btn" onClick={() => runRestore(false)}
                disabled={oneTime.busy.startsWith("restore")} data-testid="onetime-restore-preview">
                {oneTime.busy === "restore:preview" ? "Previewing…" : "Preview"}
              </button>
              <button className="jde-btn jde-btn-primary" onClick={() => runRestore(true)}
                disabled={oneTime.busy.startsWith("restore") || !oneTime.restore} data-testid="onetime-restore-apply">
                {oneTime.busy === "restore:apply" ? "Restoring…" : "Apply"}
              </button>
            </div>
            {oneTime.restore && (
              <div data-testid="onetime-restore-result" style={{ marginTop: 10, fontSize: 12, background: oneTime.restore.applied ? "#ECFDF5" : "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: 10 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{oneTime.restore.applied ? "APPLIED" : "PREVIEW"}</div>
                <div>Invoices restored: <b>{oneTime.restore.docs_restored}</b> · orphan lines: <b>{oneTime.restore.orphan_lines_restored}</b> · already present: <b>{oneTime.restore.skipped_already_present}</b> · fully covered (left deleted): <b>{oneTime.restore.skipped_fully_covered}</b></div>
                {(oneTime.restore.detail || []).slice(0, 20).map((d, i) => (
                  <div key={i} style={{ marginTop: 4 }}>
                    INV <b>{d.inv}</b> ({d.pcode}): {(d.orphan_lines || []).map((l) => `${l.po}/${l.item} ×${l.qty}`).join(", ")}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="jde-panel" style={{ marginTop: 12 }}>
        <div className="jde-panel-header"><span>FUTURE: SAP DIRECT INTEGRATION</span></div>
        <div className="jde-panel-body muted" style={{ fontSize: 12 }}>
          Direct SAP invoice push (IDOC / OData) will be added in a future phase. For now, SAP invoices are captured from forwarded email body.
        </div>
      </div>
    </Layout>
  );
}
